/**
 * `<CheatsheetApp>` — the content of the GM desktop's **CHEATSHEET.app** window: a paginated,
 * visually designed rules reference for the things a GM adjudicates at the table but the app
 * doesn't run for them. A page rail on the left, a page pane on the right, driven by
 * {@link CHEATSHEET_PAGES} — a registry of `{ id, title, Component }`. Adding a page is an
 * entry plus a component, nothing else; this component never special-cases a page by id.
 *
 * The selected page is the rail page's URL-bearing sub-page (`/cheatsheet/<pageId>`): the shell
 * owns it through `useRail` and threads it in as `page`/`onPage`, exactly like COMPENDIUM's —
 * this component only renders the rail + pane and reports a rail click back through `onPage`.
 *
 * Deliberately static — no calculator, no dice widget. Styled by `cheatsheet.css`; the
 * `gms-cheatsheet-` class family below is that stylesheet's hook.
 */
import type { ReactNode } from "react";
import { AtTheTablePage, SessionZeroPage, SettingPage } from "./PlayerOnboardingPages";
import { RepairsPage } from "./RepairsPage";
import { SkillChecksPage } from "./SkillChecksPage";
import "./cheatsheet.css";

/** The selected page of the cheatsheet, by registry id. */
export type CheatsheetPage = { pageId: string };

/** One entry in the page registry: an id (the URL param + rail key), a rail label, and the page's content component. */
export type CheatsheetPageEntry = {
  id: string;
  title: string;
  Component: () => ReactNode;
};

/** The real page registry. */
export const CHEATSHEET_PAGES: CheatsheetPageEntry[] = [
  { id: "skill-checks", title: "SKILL CHECKS", Component: SkillChecksPage },
  { id: "setting", title: "THE SETTING", Component: SettingPage },
  { id: "at-the-table", title: "AT THE TABLE", Component: AtTheTablePage },
  { id: "repairs", title: "REPAIRS & REST", Component: RepairsPage },
  { id: "session-zero", title: "SESSION ZERO", Component: SessionZeroPage },
];

/** The player desktop starts with the Session 0 primer, not the GM-facing skill-check page. */
export const PLAYER_CHEATSHEET_PAGES = CHEATSHEET_PAGES.filter((page) => page.id !== "skill-checks");

export function CheatsheetApp({
  page,
  onPage,
  pages = CHEATSHEET_PAGES,
}: {
  /** The window's URL-bearing sub-page — which registry entry is selected. */
  page: CheatsheetPage;
  onPage: (next: CheatsheetPage) => void;
  /** The page registry to render against. Defaults to the real {@link CHEATSHEET_PAGES}; overridable for tests. */
  pages?: CheatsheetPageEntry[];
}) {
  const active = pages.find((p) => p.id === page.pageId) ?? pages[0];

  return (
    <div className="gms-cheatsheet">
      <div className="gms-cheatsheet-rail">
        {pages.map((p) => (
          <button
            key={p.id}
            type="button"
            className="gms-cheatsheet-rail-item"
            aria-pressed={p.id === active?.id}
            onClick={() => onPage({ pageId: p.id })}
          >
            {p.title}
          </button>
        ))}
      </div>
      <div className="gms-cheatsheet-pane">{active ? <active.Component /> : <div className="gms-content-message">No pages loaded.</div>}</div>
    </div>
  );
}
